import { Gauge } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { ApiError, getKnowledgeScore } from "@/api/client";
import type { KnowledgeScore, PartScore } from "@/api/client";
import { NothingLearned } from "@/screens/knowledge/NothingLearned";
import { Badge } from "@/ui/Badge";
import { EmptyState } from "@/ui/EmptyState";
import { Notice } from "@/ui/Notice";
import { Card, Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

function pct(right: number, of: number): string {
  return of ? `${Math.round((100 * right) / of)}%` : "–";
}

function n(value: number): string {
  return value.toLocaleString("en-US");
}

/** Each part of the code, how often the library predicts it, weakest first. */
function PartsTable({ parts }: { parts: PartScore[] }) {
  const sorted = [...parts].sort((a, b) => a.right / (a.scored || 1) - b.right / (b.scored || 1));
  return (
    <Table aria-label="By part">
      <thead>
        <tr>
          <Th>Part</Th>
          <Th className="text-right">Right</Th>
          <Th className="text-right">Panels</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((p) => (
          <tr key={p.key}>
            <Td>{p.label}</Td>
            <Td className="text-right tabular-nums">{pct(p.right, p.scored)}</Td>
            <Td className="text-right text-muted tabular-nums">{n(p.scored)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/**
 * The knowledge score (ADR-0014): each past take-off predicted from what the others
 * taught, for the full code and part by part, and the configurations to upload next.
 */
export function ScoreTab() {
  const [score, setScore] = useState<KnowledgeScore | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getKnowledgeScore()
      .then((s) => live && setScore(s))
      .catch(
        (err: unknown) =>
          live && setError(err instanceof ApiError ? err.detail : "Could not reach the server."),
      );
    return () => {
      live = false;
    };
  }, []);

  if (score === null) {
    return error ? (
      <Notice tone="danger">{error}</Notice>
    ) : (
      <div className="space-y-2" role="status" aria-label="Loading the score">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (score.mode === "none") {
    return (
      <>
        <EmptyState icon={<Gauge />} title="Nothing to score yet">
          Add a past take-off under <Link to="/knowledge/sources">Sources</Link>. With two or more,
          each is predicted from the others, and the score says how often the codes come out right.
        </EmptyState>
        <NothingLearned />
      </>
    );
  }

  const inSample = score.mode === "in_sample";
  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-2 p-5" role="region" aria-label="Headline">
        <div className="flex items-center gap-2">
          <span className="text-3xl font-semibold text-ink tabular-nums">
            {pct(score.exact, score.covered)}
          </span>
          <span className="text-sm text-muted">of codes right in full</span>
          {inSample ? <Badge tone="warn">In-sample</Badge> : null}
        </div>
        <p className="text-sm text-ink-2">
          {inSample
            ? "Only one past take-off, so it is scored against what it taught itself. Add another to see how well that carries to a new job."
            : "Each past take-off without its own votes, predicted from what the others taught."}
        </p>
        <p className="text-xs text-muted">
          {n(score.covered)} of {n(score.panels)} panels are of configurations the library could
          predict ({pct(score.covered, score.panels)}).
          {score.standard === "proposed"
            ? " Parts are read with the proposed code standard; confirm it under Code standard."
            : ""}
        </p>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {score.parts.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-ink">By part</h3>
            <PartsTable parts={score.parts} />
          </section>
        ) : null}
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-ink">By take-off</h3>
          <Table aria-label="By take-off">
            <thead>
              <tr>
                <Th>Take-off</Th>
                <Th className="text-right">Covered</Th>
                <Th className="text-right">Right</Th>
              </tr>
            </thead>
            <tbody>
              {score.jobs.map((j) => (
                <tr key={j.document_id}>
                  <Td>
                    <Link to={`/knowledge/sources/${j.document_id}`}>{j.filename}</Link>
                  </Td>
                  <Td className="text-right tabular-nums">{pct(j.covered, j.panels)}</Td>
                  <Td className="text-right tabular-nums">{pct(j.exact, j.covered)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      </div>

      {score.gaps.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-ink">What to upload next</h3>
          <p className="text-xs text-muted">
            No other take-off has these configurations. A past job that has them raises the score
            most.
          </p>
          <ul className="flex flex-col gap-1 text-sm">
            {score.gaps.map((g) => (
              <li key={g.config} className="flex justify-between gap-4">
                <span className="font-mono text-ink-2">{g.config}</span>
                <span className="text-muted tabular-nums">{n(g.panels)} panels</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
